import { HttpHeaders } from "@angular/common/http";
import { environment } from "../../../environments/environment";

export default class Constants {
  /**
   * Application constants
   * This class holds various constants used throughout the application
   */

  // API base URL
  static readonly API_BASE_URL: string = environment.API_URL;

  // Default timeout for HTTP requests in milliseconds
  static readonly HTTP_TIMEOUT: number = 30000; // 30 seconds

  // Maximum number of retries for failed HTTP requests
  static readonly MAX_HTTP_RETRIES: number = 0; // Retry once on failure

  // Default page size for paginated API responses
  static readonly DEFAULT_PAGE_SIZE: number = 20;

  // Default header for API requests
  static readonly DEFAULT_HEADERS: HttpHeaders = new HttpHeaders({
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  });

  // Error messages
  static readonly ERROR_MESSAGES = {
    NETWORK_ERROR: 'Network error occurred. Please try again later.',
    UNAUTHORIZED: 'You are not authorized to perform this action.',
    NOT_FOUND: 'Requested resource not found.',
    SERVER_ERROR: 'An unexpected error occurred on the server.'
  };

  // Accepted UTM query params (from marketing links) passed to create customer API; any other params are ignored
  static readonly ALLOWED_UTM_PARAMS: string[] = ['utm_source', 'utm_medium', 'utm_id', 'utm_content'];

  // Fuel types
  static readonly FUEL_TYPES = {
    ELECTRICITY: 'electricity',
    GAS: 'gas'
  };

  // Regex patterns for UK standards validation
  static readonly REGEX = {
    // UK Postcode: Supports all valid UK postcode formats (e.g., M1 1AA, M60 1NW, CR0 2YR, DN55 1PT, W1A 0AX, EC1A 1BB)
    POSTCODE: /^[A-Z]{1,2}[0-9][A-Z0-9]?\s?[0-9][A-Z]{2}$/i,
    
    // Email: RFC 5322 compliant pattern
    EMAIL: /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/,
    
    // UK Phone: Mobile (07xxx) and Landline (01xxx, 02xxx - 11 digits)
    // Supports both UK format (0x) and international format (+44x)
    PHONE: /^(?:(?:\+44|0)(?:7|1|2)\d{9})$/,
    
    // MPAN: 13-digit electricity meter point administration number (e.g., 1012345678901)
    MPAN: /^\d{13}$/,
    
    // MPRN: 6-10 digit gas meter point reference number (e.g., 1234567890)
    MPRN: /^\d{6,10}$/,
    
    // UK Business Registration Number (Company House number): 8 digits, can have leading zeros
    COMPANY_NUMBER: /^(?:0[0-9]|[0-9]{2})\d{6}$/,
    
    // First Name: 2-50 characters, alphabetic, spaces, hyphens, apostrophes allowed
    FIRST_NAME: /^[a-zA-Z][a-zA-Z\s\-']{0,48}[a-zA-Z]$|^[a-zA-Z]{2}$/,
    
    // Last Name: 2-50 characters, alphabetic, spaces, hyphens, apostrophes allowed
    LAST_NAME: /^[a-zA-Z][a-zA-Z\s\-']{0,48}[a-zA-Z]$|^[a-zA-Z]{2}$/,
    
    // UK Sort Code: 6 digits, can be XXXXXX
    SORT_CODE: /^\d{6}$/,
    
    // UK Bank Account Number: 8 digits
    ACCOUNT_NUMBER: /^\d{8}$/,
    
    // UK Account Holder Name: 1-70 characters, letters, spaces, hyphens, apostrophes allowed
    ACCOUNT_HOLDER_NAME: /^[a-zA-Z][a-zA-Z\s\-'\.]{0,68}[a-zA-Z]$|^[a-zA-Z]$/
  };

  // Banner placement
  static readonly BANNER_PLACEMENT = {
    DASHBOARD_BANNER: 'DASHBOARD_BANNER',
    METER_CONFIRMATION_BANNER: 'METER_CONFIRMATION_BANNER',
    METER_REVIEW_BANNER: 'METER_REVIEW_BANNER',
    QUOTES_BANNER: 'QUOTES_BANNER',
    APP_BANNER: 'APP_BANNER'
  };

  // Route to Banner Placement Mapping
  static readonly ROUTE_BANNER_MAPPING: { [key: string]: string } = {
    '/': 'APP_BANNER',
    '/dashboard': 'DASHBOARD_BANNER',
    '/meter-confirmation': 'METER_CONFIRMATION_BANNER',
    '/meter-review': 'METER_REVIEW_BANNER',
    '/quotes': 'QUOTES_BANNER',
    '/contact-details': 'APP_BANNER',
    '/business-details': 'APP_BANNER',
    '/application-review': 'APP_BANNER',
    '/switch-confirmation': 'APP_BANNER'
  };

  /**
   * Get banner placement based on the current route
   * @param route - The current route URL
   * @returns The banner placement key
   */
  static getBannerPlacementByRoute(route: string): string {
    // Remove query parameters and fragments
    const cleanRoute = route.split('?')[0].split('#')[0];
    
    // Handle dynamic routes (e.g., /quotes/:data)
    if (cleanRoute.startsWith('/quotes/')) {
      return this.ROUTE_BANNER_MAPPING['/quotes'] || '';
    }
    
    // Return mapped placement or default to APP_BANNER
    return this.ROUTE_BANNER_MAPPING[cleanRoute] || '';
  }
}